import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert, Avatar, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
  Container, FormControl, FormControlLabel, InputLabel, MenuItem, Rating, Select, Stack, TextField, Typography,
} from '@mui/material';
import { ArrowBack, OpenInNew, School } from '@mui/icons-material';
import { apiGetProfessor, apiUpsertProfessorReview } from '../../services/api';
import { initials } from '../../utils/strings';

const AVATAR_GRAD = 'linear-gradient(140deg,#FFC24B 0%,#FF7A36 60%,#FF5B2E 100%)';
const OTHER_COURSE = '__other__';

// The courses this professor is associated with: native course links plus the courses RMP
// lists them teaching, de-duplicated. Powers the review form's course dropdown.
function courseOptionsFrom(d: any): string[] {
  const native = (d?.courses || []).map((c: any) => c?.code).filter(Boolean);
  const rmp = (d?.rmpCourseCodes || []).filter(Boolean);
  return Array.from(new Set<string>([...native, ...rmp])).sort();
}

const ProfessorDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [rating, setRating] = useState<number | null>(null);
  const [difficulty, setDifficulty] = useState<number | null>(null);
  const [wouldTakeAgain, setWouldTakeAgain] = useState(false);
  const [courseCode, setCourseCode] = useState('');
  const [customCourse, setCustomCourse] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [formMsg, setFormMsg] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const d = await apiGetProfessor(id);
      setData(d);
      if (d.myReview) {
        setRating(d.myReview.rating ?? null);
        setDifficulty(d.myReview.difficulty ?? null);
        setWouldTakeAgain(!!d.myReview.wouldTakeAgain);
        const mc = d.myReview.courseCode ?? '';
        const opts = courseOptionsFrom(d);
        if (!mc) { setCourseCode(''); setCustomCourse(''); }
        else if (opts.includes(mc)) { setCourseCode(mc); setCustomCourse(''); }
        else { setCourseCode(OTHER_COURSE); setCustomCourse(mc); }
        setContent(d.myReview.content ?? '');
      }
      setError(null);
    } catch (e: any) {
      setError(e?.message === 'not_found' ? 'Professor not found.' : (e?.message || 'Failed to load professor'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const submit = async () => {
    if (!id || !rating) { setFormMsg('Please choose an overall rating (1–5).'); return; }
    setSaving(true);
    setFormMsg(null);
    try {
      const finalCourse = courseCode === OTHER_COURSE ? (customCourse.trim() || null) : (courseCode || null);
      await apiUpsertProfessorReview(id, { rating, difficulty, wouldTakeAgain, courseCode: finalCourse, content: content || null });
      setFormMsg('Saved!');
      await load();
    } catch (e: any) {
      setFormMsg(e?.message || 'Could not save your review');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Container sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Container>;
  if (error || !data) {
    return (
      <Container sx={{ py: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/professors')}>All professors</Button>
        <Alert severity="error" sx={{ mt: 2 }}>{error || 'Not found'}</Alert>
      </Container>
    );
  }

  const courseOptions = courseOptionsFrom(data);

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/professors')} sx={{ mb: 2 }}>All professors</Button>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, minWidth: 0 }}>
              <Avatar sx={{ background: AVATAR_GRAD, color: '#fff', fontWeight: 700, width: 56, height: 56, fontSize: 22, flexShrink: 0 }}>
                {initials(data.name)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }}>{data.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', mt: 0.5 }}>
                  <School fontSize="small" /><Typography variant="body2">{data.department || 'Faculty'}</Typography>
                </Box>
                <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {(data.courses || []).map((c: any) => (
                    <Chip key={c.id} label={c.code} size="small" clickable onClick={() => navigate('/courses', { state: { openCourseId: c.id, openCourseCode: c.code } })} />
                  ))}
                </Box>
              </Box>
            </Box>
            <Box sx={{ textAlign: 'center', minWidth: 90 }}>
              <Typography variant="h3" sx={{ fontWeight: 800, color: 'secondary.main' }}>{data.avgRating ?? '—'}</Typography>
              <Typography variant="caption" color="text.secondary">{data.numReviews} Dragonsframe review{data.numReviews === 1 ? '' : 's'}</Typography>
            </Box>
          </Box>

          {data.numReviews > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="overline" color="text.secondary">From Dragonsframe students</Typography>
              <Stack direction="row" spacing={3} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 1 }}>
                <Typography variant="body2"><b>{data.wouldTakeAgainPct != null ? `${data.wouldTakeAgainPct}%` : '—'}</b> would take again</Typography>
                <Typography variant="body2"><b>{data.avgDifficulty ?? '—'}</b> avg difficulty</Typography>
              </Stack>
            </Box>
          )}

          {/* RateMyProfessors aggregates — public data shown with attribution; details via link-out. */}
          <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
            <Typography variant="overline" color="text.secondary">From RateMyProfessors</Typography>
            {(data.rmpRating != null || data.rmpWouldTakeAgain != null || data.rmpDifficulty != null) ? (
              <Stack direction="row" spacing={3} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 1 }}>
                {data.rmpRating != null && (
                  <Typography variant="body2"><b>{data.rmpRating}★</b> overall{data.rmpNumRatings != null ? ` (${data.rmpNumRatings})` : ''}</Typography>
                )}
                {data.rmpWouldTakeAgain != null && (
                  <Typography variant="body2"><b>{data.rmpWouldTakeAgain}%</b> would take again</Typography>
                )}
                {data.rmpDifficulty != null && (
                  <Typography variant="body2"><b>{data.rmpDifficulty}</b> avg difficulty</Typography>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>No cached ratings — open their RateMyProfessors page below.</Typography>
            )}
            {((data.rmpCourses?.length ?? 0) > 0 || (data.rmpCourseCodes || []).length > 0) && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Courses taught (per RateMyProfessors)</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {(data.rmpCourses ?? (data.rmpCourseCodes || []).map((c: string) => ({ code: c, courseId: null })))
                    .slice(0, 24)
                    .map((rc: any, i: number) => (
                      rc.courseId
                        ? <Chip key={rc.code + i} label={rc.code} size="small" variant="outlined" clickable onClick={() => navigate('/courses', { state: { openCourseId: rc.courseId, openCourseCode: rc.code } })} />
                        : <Chip key={rc.code + i} label={rc.code} size="small" variant="outlined" />
                    ))}
                </Box>
              </Box>
            )}
            <Button variant="outlined" color="secondary" endIcon={<OpenInNew />} href={data.rmpUrl} target="_blank" rel="noopener noreferrer" sx={{ mt: 1.5 }}>
              View full profile on RateMyProfessors
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{data.myReview ? 'Your review' : 'Rate this professor'}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Reviews are anonymous and limited to one per student — you can update yours anytime.
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography component="legend" variant="body2">Overall *</Typography>
              <Rating value={rating} onChange={(_, v) => setRating(v)} />
            </Box>
            <Box>
              <Typography component="legend" variant="body2">Difficulty</Typography>
              <Rating value={difficulty} onChange={(_, v) => setDifficulty(v)} />
            </Box>
            <FormControlLabel
              control={<Checkbox checked={wouldTakeAgain} onChange={(e) => setWouldTakeAgain(e.target.checked)} />}
              label="I would take this professor again"
            />
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="course-sel-label">Course (optional)</InputLabel>
                <Select labelId="course-sel-label" label="Course (optional)" value={courseCode} onChange={(e) => setCourseCode(e.target.value)}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  {courseOptions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  <MenuItem value={OTHER_COURSE}>Other…</MenuItem>
                </Select>
              </FormControl>
              {courseCode === OTHER_COURSE && (
                <TextField size="small" label="Course code" value={customCourse} onChange={(e) => setCustomCourse(e.target.value)} placeholder="e.g. CS 171" sx={{ maxWidth: 200 }} />
              )}
            </Box>
            <TextField label="Your review (optional)" value={content} onChange={(e) => setContent(e.target.value)} multiline minRows={3} inputProps={{ maxLength: 2000 }} />
            {formMsg && <Alert severity={formMsg === 'Saved!' ? 'success' : 'error'}>{formMsg}</Alert>}
            <Box><Button variant="contained" onClick={submit} disabled={saving}>{saving ? 'Saving…' : data.myReview ? 'Update anonymous review' : 'Post anonymously'}</Button></Box>
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ mb: 1, fontWeight: 800 }}>Student reviews</Typography>
      {(data.reviews || []).length === 0 ? (
        <Typography color="text.secondary">No reviews yet — be the first.</Typography>
      ) : (
        <Stack spacing={2}>
          {(data.reviews || []).map((r: any) => (
            <Card key={r.id}>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {/* Anonymous: empty Avatar renders MUI's neutral person silhouette. */}
                  <Avatar sx={{ bgcolor: 'action.selected', color: 'text.secondary', width: 40, height: 40 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2">{r.isMine ? 'Your review' : 'Anonymous student'}</Typography>
                      <Rating value={r.rating} readOnly size="small" />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {r.courseCode ? `${r.courseCode} · ` : ''}
                      {r.wouldTakeAgain ? 'Would take again · ' : ''}
                      {r.difficulty ? `Difficulty ${r.difficulty}/5 · ` : ''}
                      {new Date(r.createdAt).toLocaleDateString()}
                    </Typography>
                    {r.content && <Typography variant="body2" sx={{ mt: 0.5 }}>{r.content}</Typography>}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Container>
  );
};

export default ProfessorDetail;
